import React, { useEffect, useMemo, useState } from "react";
import { FiCalendar, FiCheck, FiList, FiUser } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import {
  getRosterInviteAvailability,
  respondToRosterInviteAvailability,
} from "../../api/roster";
import logoLight from "../../assets/imgs/logo-transparent-light.gif";
import PhoneInput from "../../components/PhoneInput/PhoneInput";
import {
  formatOccurrenceDate,
  formatSlotOccurrenceTimeRange,
  toCalendarWallDate,
  toOccurrenceDateTime,
} from "../../util/eventDateTime";

const toInviteDate = (value, event) => toCalendarWallDate(value, event);

const toOccurrenceLocalDate = (value, event) => {
  const dt = toOccurrenceDateTime(value, event);
  if (!dt?.isValid) return null;
  return new Date(dt.year, dt.month - 1, dt.day);
};

const getDayKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "date-tba";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const slotItemKey = (item) =>
  `${item.slot?._id}:${new Date(item.occurrenceStart).getTime()}`;

const groupSlotsByEvent = (items = []) => {
  const groups = new Map();
  for (const item of items) {
    const eventId = item.event?._id || item.slot?._id || "event-tba";
    if (!groups.has(eventId)) {
      groups.set(eventId, { event: item.event, items: [] });
    }
    groups.get(eventId).items.push(item);
  }
  return Array.from(groups.values());
};

const JoinRosterPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState("loading");
  const [invite, setInvite] = useState(null);
  const [profile, setProfile] = useState({ name: "", email: "", phone: "", note: "" });
  const [selectedSlots, setSelectedSlots] = useState({});
  const [slotNotes, setSlotNotes] = useState({});
  const [viewMode, setViewMode] = useState("calendar");
  const [submission, setSubmission] = useState(null);
  const [ownerImageFailed, setOwnerImageFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      return;
    }

    getRosterInviteAvailability(token)
      .then(({ invite: inviteData }) => {
        setInvite(inviteData);
        setProfile({
          name: inviteData?.profile?.name || "",
          email: inviteData?.email || "",
          phone: inviteData?.profile?.phone || "",
          note: inviteData?.profile?.note || "",
        });
        setStatus("ready");
      })
      .catch((err) => {
        const code = err?.response?.data?.error;
        setStatus(code === "invite_expired" ? "expired" : "error");
      });
  }, [token]);

  const slots = useMemo(() => {
    if (!invite?.events) return [];
    return invite.events
      .flatMap((event) =>
        (event.openSlots || []).map(({ slot, occurrenceStart, occurrenceEnd }) => ({
          event,
          slot,
          occurrenceStart,
          occurrenceEnd,
        }))
      )
      .sort((a, b) => {
        const aStart = toOccurrenceDateTime(a.occurrenceStart, a.event)?.toMillis() || 0;
        const bStart = toOccurrenceDateTime(b.occurrenceStart, b.event)?.toMillis() || 0;
        return aStart - bStart;
      });
  }, [invite]);

  const selectedCount = useMemo(
    () => Object.values(selectedSlots).filter(Boolean).length,
    [selectedSlots]
  );
  const hasOpenSlots = slots.length > 0;

  const calendarDays = useMemo(() => {
    const grouped = new Map();
    for (const item of slots) {
      const start = toOccurrenceLocalDate(item.occurrenceStart, item.event);
      if (!start || Number.isNaN(start.getTime())) continue;
      const key = getDayKey(start);
      const date = new Date(start);
      if (!grouped.has(key)) {
        grouped.set(key, { key, date, items: [] });
      }
      grouped.get(key).items.push(item);
    }
    return Array.from(grouped.values()).sort(
      (a, b) => a.date.getTime() - b.date.getTime()
    );
  }, [slots]);

  const formatDateRange = (event = {}) => {
    const start = toInviteDate(event?.date?.start, event);
    const end = toInviteDate(event?.date?.end, event);
    if (!start || Number.isNaN(start.getTime())) return "date to be announced";

    const startText = start.toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
    if (!end || Number.isNaN(end.getTime())) return startText;

    return `${startText} - ${end.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    })}`;
  };

  const formatDay = (date) => {
    const start = date instanceof Date ? date : null;
    if (!start || Number.isNaN(start.getTime())) return "date tba";
    return start.toLocaleDateString([], {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const formatTimeRange = (event = {}) => {
    const start = toInviteDate(event?.date?.start, event);
    const end = toInviteDate(event?.date?.end, event);
    if (!start || Number.isNaN(start.getTime())) return "";
    const startText = start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    if (!end || Number.isNaN(end.getTime())) return startText;
    return `${startText} - ${end.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    })}`;
  };

  const toggleSlot = (itemKey) => {
    setSelectedSlots((current) => ({
      ...current,
      [itemKey]: !current[itemKey],
    }));
  };

  const setSlotNote = (itemKey, value) => {
    setSlotNotes((current) => ({
      ...current,
      [itemKey]: value,
    }));
  };

  const validate = () => {
    if (!profile.name.trim()) return "add your name before submitting.";
    return "";
  };

  const submitAvailability = async (event) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const data = await respondToRosterInviteAvailability(token, {
        profile,
        availability: slots.map((item) => ({
          eventId: item.event._id,
          slotId: item.slot._id,
          occurrenceStart: item.occurrenceStart,
          status: selectedSlots[slotItemKey(item)] ? "available" : "unavailable",
          note: slotNotes[slotItemKey(item)] || "",
        })),
      });
      if (data?.accountCreated === false) {
        if (data?.token) localStorage.setItem("jwt_token", data.token);
        window.location.hash = "#/";
        return;
      }
      setSubmission(data);
      setStatus("submitted");
    } catch (err) {
      const code = err?.response?.data?.error;
      setError(
        code === "email_mismatch"
          ? "this invite is tied to a different email address."
          : code === "invite_expired"
          ? "this invite link has expired. ask the venue to send a new one."
          : "we could not submit your availability. try again in a moment."
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSubmitting(false);
    }
  };

  const ownerName =
    invite?.roster?.owner?.businessName ||
    invite?.roster?.owner?.username ||
    invite?.roster?.displayName;
  const ownerImage = invite?.roster?.displayPicture || invite?.roster?.owner?.profilePicture;
  const showOwnerImage = ownerImage && !ownerImageFailed;
  const rosterName = invite?.roster?.displayName || "this roster";
  const isExistingAccountInvite = Boolean(invite?.existingAccount);

  const exploreAccount = () => {
    if (submission?.token) {
      localStorage.setItem("jwt_token", submission.token);
      sessionStorage.setItem("guest_profile_prompt_source", "roster-invite");
    }
    window.location.hash = "#/";
  };

  const exploreEvents = () => {
    if (submission?.token) localStorage.setItem("jwt_token", submission.token);
    window.location.hash = "#/";
  };

  if (status === "ready") {
    return (
      <main className="h-[100dvh] overflow-y-auto bg-[var(--inyc-primary)] px-kit-16 py-kit-20 text-[var(--inyc-primary-text-color)] md:py-kit-32">
        <form
          onSubmit={submitAvailability}
          className="mx-auto flex w-full max-w-[980px] flex-col gap-kit-16 pb-kit-32"
        >
          <section className="relative overflow-hidden rounded-kit-xl border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] p-kit-20 shadow-kit-card md:p-kit-24">
            <div className="absolute right-[-4rem] top-[-4rem] h-[11rem] w-[11rem] rounded-kit-full bg-[var(--inyc-secondary)] opacity-[0.08]" />
            <div className="relative flex items-center justify-between gap-kit-16">
              <img
                src={logoLight}
                alt="in-yc"
                className="h-[3.4rem] w-auto invert-[var(--inyc-invert-black)]"
              />
              <div className="flex items-center gap-kit-10 rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-input-background-color)] px-kit-12 py-kit-8">
                {showOwnerImage ? (
                  <img
                    src={ownerImage}
                    alt=""
                    onError={() => setOwnerImageFailed(true)}
                    className="h-[2rem] w-[2rem] rounded-kit-full object-cover"
                  />
                ) : (
                  <div className="flex h-[2rem] w-[2rem] items-center justify-center rounded-kit-full bg-[var(--inyc-secondary)] text-[var(--inyc-secondary-text-color)]">
                    <FiUser size={14} />
                  </div>
                )}
                <div className="min-w-0">
                  <div className="max-w-[9rem] truncate font-['Primary-Bold'] text-kit-xs lowercase md:max-w-[16rem]">
                    {ownerName}
                  </div>
                  <div className="text-kit-2xs lowercase text-[var(--inyc-primary-text-color-light)]">
                    invited you
                  </div>
                </div>
              </div>
            </div>
            <div className="relative mt-kit-24 font-['Primary-Bold'] text-kit-2xs uppercase tracking-[0.18em] text-[var(--inyc-primary-text-color-light)]">
              {rosterName}
            </div>
            <h1 className="relative mt-kit-6 font-['Primary-Bold'] text-kit-2xl lowercase leading-[0.95] md:text-[4rem]">
              {hasOpenSlots ? "share your availability" : "join this roster"}
            </h1>
            <p className="relative mt-kit-16 max-w-[650px] text-kit-sm leading-[1.55] text-[var(--inyc-primary-text-color-light)]">
              {hasOpenSlots
                ? "Tap the slots you can do. Everything you leave unselected stays opted out by default, so this should only take a moment."
                : "There are no open slots to choose from right now. Confirm below and you'll join the roster."}
            </p>
            {error && (
              <div className="mt-kit-16 rounded-kit-md border-[1px] border-danger-line bg-danger-soft px-kit-16 py-kit-12 font-['Primary-Medium'] text-kit-sm text-danger">
                {error}
              </div>
            )}
          </section>

          <section className="grid gap-kit-16 rounded-kit-xl border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-input-background-color)] p-kit-20 md:grid-cols-2 md:p-kit-24">
            <label className="flex flex-col gap-kit-6">
              <span className="font-['Primary-Bold'] text-kit-xs lowercase">name</span>
              <input
                value={profile.name}
                onChange={(event) =>
                  setProfile((current) => ({ ...current, name: event.target.value }))
                }
                disabled={isExistingAccountInvite}
                className={`rounded-kit-lg border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] px-kit-16 py-kit-12 outline-none ${
                  isExistingAccountInvite ? "text-[var(--inyc-primary-text-color-light)]" : ""
                }`}
                placeholder="your name or stage name"
                maxLength={140}
              />
              {isExistingAccountInvite && (
                <span className="text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
                  using the name from your in-yc account
                </span>
              )}
            </label>
            <label className="flex flex-col gap-kit-6">
              <span className="font-['Primary-Bold'] text-kit-xs lowercase">email</span>
              <input
                value={profile.email}
                disabled
                className="rounded-kit-lg border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] px-kit-16 py-kit-12 text-[var(--inyc-primary-text-color-light)] outline-none"
              />
            </label>
            <PhoneInput
              label="phone optional"
              value={profile.phone}
              onChange={(phone) =>
                setProfile((current) => ({ ...current, phone }))
              }
              placeholder="best contact number"
              variant="compact"
            />
            <label className="flex flex-col gap-kit-6 md:col-span-2">
              <span className="font-['Primary-Bold'] text-kit-xs lowercase">note optional</span>
              <textarea
                value={profile.note}
                onChange={(event) =>
                  setProfile((current) => ({ ...current, note: event.target.value }))
                }
                className="min-h-[6rem] rounded-kit-lg border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] px-kit-16 py-kit-12 outline-none"
                placeholder="anything the venue should know"
                maxLength={1000}
              />
            </label>
          </section>

          <section className="rounded-kit-xl border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] p-kit-20 md:p-kit-24">
            <div className="mb-kit-16 flex flex-wrap items-end justify-between gap-kit-12">
              <div>
                <div className="font-['Primary-Bold'] text-kit-md lowercase">
                  open slots
                </div>
                <div className="mt-kit-4 text-kit-sm text-[var(--inyc-primary-text-color-light)]">
                  {hasOpenSlots
                    ? `${selectedCount} selected, unselected slots are opted out`
                    : "no slot selection needed"}
                </div>
              </div>
              {hasOpenSlots && (
                <div className="flex rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-input-background-color)] p-kit-4">
                {[
                  { id: "calendar", label: "calendar", icon: FiCalendar },
                  { id: "list", label: "list", icon: FiList },
                ].map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setViewMode(id)}
                    className={`flex items-center gap-kit-6 rounded-kit-full px-kit-12 py-kit-6 font-['Primary-Bold'] text-kit-2xs lowercase transition-colors duration-200 ${
                      viewMode === id
                        ? "bg-[var(--inyc-secondary)] text-[var(--inyc-secondary-text-color)]"
                        : "text-[var(--inyc-primary-text-color-light)]"
                    }`}
                  >
                    <Icon size={13} />
                    {label}
                  </button>
                ))}
                </div>
              )}
            </div>

            {slots.length === 0 ? (
              <div className="rounded-kit-lg border-[1px] border-dashed border-[var(--inyc-input-border-color)] p-kit-20 text-center text-kit-sm text-[var(--inyc-primary-text-color-light)]">
                there are no open slots on this roster right now. confirm below to approve joining the roster.
              </div>
            ) : viewMode === "calendar" ? (
              <div className="overflow-x-auto pb-kit-6">
                <div className="flex min-w-max gap-kit-8">
                  {calendarDays.map(({ key, date, items }) => (
                  <div
                    key={key}
                    className="min-h-[14rem] w-[10.75rem] rounded-kit-lg border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-input-background-color)] p-kit-10 md:w-[12rem]"
                  >
                    <div className="mb-kit-10 border-b-[1px] border-[var(--inyc-input-border-color)] pb-kit-8">
                      <div className="font-['Primary-Bold'] text-kit-sm lowercase">
                        {formatDay(date)}
                      </div>
                      <div className="mt-kit-2 text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
                        {items.length} slot{items.length === 1 ? "" : "s"}
                      </div>
                    </div>
                    <div className="flex min-h-[10rem] flex-col gap-kit-12 rounded-kit-md bg-[var(--inyc-primary)] p-kit-6">
                      {items.length === 0 && (
                        <div className="flex flex-1 items-center justify-center rounded-kit-md border-[1px] border-dashed border-[var(--inyc-input-border-color)] text-center text-kit-2xs lowercase text-[var(--inyc-primary-text-color-light)]">
                          no slots
                        </div>
                      )}
                      {groupSlotsByEvent(items).map(({ event, items: eventItems }) => (
                        <div
                          key={event?._id || eventItems[0]?.slot?._id}
                          className="rounded-kit-lg border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-input-background-color)] p-kit-8"
                        >
                          <div className="mb-kit-8 border-b-[1px] border-[var(--inyc-input-border-color)] px-kit-4 pb-kit-8">
                            <div className="truncate font-['Primary-Bold'] text-kit-xs leading-[1.15]">
                              {event?.name || "event"}
                            </div>
                            <div className="mt-kit-2 text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
                              {formatTimeRange(event)} · {eventItems.length} slot{eventItems.length === 1 ? "" : "s"}
                            </div>
                          </div>
                          <div className="flex flex-col gap-kit-6">
                            {eventItems.map((item) => {
                              const itemKey = slotItemKey(item);
                              const selected = !!selectedSlots[itemKey];
                              return (
                                <button
                                  key={itemKey}
                                  type="button"
                                  onClick={() => toggleSlot(itemKey)}
                                  className={`rounded-kit-md border-[1px] px-kit-12 py-kit-10 text-left transition-all duration-200 ${
                                    selected
                                      ? "border-[var(--inyc-secondary)] bg-[var(--inyc-primary)] text-[var(--inyc-primary-text-color)] shadow-[0_0_0_2px_var(--inyc-secondary)]"
                                      : "border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] text-[var(--inyc-primary-text-color)]"
                                  }`}
                                >
                                  <div className="flex items-start justify-between gap-kit-12">
                                    <div className="min-w-0">
                                      <div className="font-['Primary-Bold'] text-kit-sm leading-[1.15]">
                                        {item.slot.title}
                                      </div>
                                      <div className={`mt-kit-4 text-kit-2xs ${selected ? "opacity-80" : "text-[var(--inyc-primary-text-color-light)]"}`}>
                                        {formatSlotOccurrenceTimeRange(
                                          item.slot,
                                          item.occurrenceStart,
                                          item.event
                                        )}
                                      </div>
                                    </div>
                                    {selected && <FiCheck className="shrink-0" size={18} />}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-kit-12">
                {slots.map((item) => {
                  const itemKey = slotItemKey(item);
                  const selected = !!selectedSlots[itemKey];
                  return (
                    <article
                      key={itemKey}
                      className={`rounded-kit-lg border-[1px] p-kit-16 transition-colors duration-200 ${
                        selected
                          ? "border-[var(--inyc-secondary)] bg-[var(--inyc-primary)] shadow-[0_0_0_2px_var(--inyc-secondary)]"
                          : "border-[var(--inyc-input-border-color)] bg-[var(--inyc-input-background-color)]"
                      }`}
                    >
                      <div className="flex flex-col gap-kit-12 md:flex-row md:items-start md:justify-between">
                        <div className="min-w-0">
                          <div className="font-['Primary-Bold'] text-kit-base leading-[1.18]">
                            {item.event.name}
                          </div>
                          <div className={`mt-kit-4 text-kit-xs ${selected ? "opacity-80" : "text-[var(--inyc-primary-text-color-light)]"}`}>
                            {formatOccurrenceDate(item.occurrenceStart, item.event, "ccc, MMM d")}
                            {item.slot?.start && (
                              <>
                                {" · "}
                                {formatSlotOccurrenceTimeRange(
                                  item.slot,
                                  item.occurrenceStart,
                                  item.event
                                )}
                              </>
                            )}
                          </div>
                          <div className="mt-kit-8 rounded-kit-full bg-[var(--inyc-primary)] px-kit-12 py-kit-6 text-kit-xs font-['Primary-Medium'] lowercase text-[var(--inyc-primary-text-color)]">
                            {item.slot.title}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleSlot(itemKey)}
                          aria-pressed={selected}
                          className={`flex shrink-0 items-center justify-center gap-kit-6 rounded-kit-full border-[1px] px-kit-16 py-kit-8 font-['Primary-Bold'] text-kit-xs lowercase transition-colors duration-200 ${
                            selected
                              ? "border-[var(--inyc-secondary)] bg-[var(--inyc-secondary)] text-[var(--inyc-secondary-text-color)]"
                              : "border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] text-[var(--inyc-primary-text-color)]"
                          }`}
                        >
                          {selected && <FiCheck size={14} />}
                          {selected ? "selected" : "opt in"}
                        </button>
                      </div>
                      {selected && (
                        <textarea
                          value={slotNotes[itemKey] || ""}
                          onChange={(event) => setSlotNote(itemKey, event.target.value)}
                          className="mt-kit-12 min-h-[4rem] w-full rounded-kit-md border-[1px] border-white/30 bg-[var(--inyc-primary)] px-kit-16 py-kit-12 text-kit-sm text-[var(--inyc-primary-text-color)] outline-none"
                          placeholder="optional note for this slot"
                          maxLength={500}
                        />
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          <button
            type="submit"
            disabled={submitting}
            className="rounded-kit-full border-[1px] border-[var(--inyc-secondary)] bg-[var(--inyc-secondary)] px-kit-20 py-kit-16 font-['Primary-Bold'] lowercase text-[var(--inyc-secondary-text-color)] shadow-kit-lg disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting
              ? "submitting..."
              : hasOpenSlots
              ? `submit ${selectedCount} selected`
              : "join roster"}
          </button>
        </form>
      </main>
    );
  }

  if (status === "submitted") {
    return (
      <main className="flex min-h-[100dvh] items-center justify-center bg-[var(--inyc-primary)] px-kit-16 py-kit-32 text-[var(--inyc-primary-text-color)]">
        <section className="w-full max-w-[460px] rounded-kit-xl border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] p-kit-20 text-center shadow-kit-card">
          <img
            src={logoLight}
            alt="in-yc"
            className="mx-auto h-[4.5rem] w-auto invert-[var(--inyc-invert-black)]"
          />
          <div className="mt-kit-16 font-['Primary-Bold'] text-kit-lg lowercase">
            {submission?.applicationCount > 0 ? "availability sent" : "roster joined"}
          </div>
          <p className="mx-auto mt-kit-10 max-w-[320px] text-kit-sm leading-[1.5] text-[var(--inyc-primary-text-color-light)]">
            {submission?.applicationCount > 0
              ? "thanks. your account was set up and your selected slots were sent to the venue for review."
              : "thanks. your account was set up and you joined the roster."}
          </p>
          <div className="mt-kit-20 flex flex-col gap-kit-10">
            <button
              type="button"
              onClick={exploreAccount}
              className="rounded-kit-full bg-[var(--inyc-secondary)] px-kit-16 py-kit-12 font-['Primary-Bold'] lowercase text-[var(--inyc-secondary-text-color)]"
            >
              explore the map
            </button>
            <button
              type="button"
              onClick={exploreEvents}
              className="rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] px-kit-16 py-kit-12 font-['Primary-Bold'] lowercase text-[var(--inyc-primary-text-color)]"
            >
              explore events
            </button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--inyc-primary)",
        color: "var(--inyc-primary-text-color)",
        padding: "var(--inyc-space-32)",
        gap: "var(--inyc-space-16)",
        textAlign: "center",
      }}
    >
      <div style={{ fontFamily: "'Primary-Bold'", fontSize: "var(--inyc-text-md)" }}>
        {status === "loading" && "loading invite..."}
        {status === "submitted" && "availability sent"}
        {status === "expired" && "invite expired"}
        {status === "error" && "invalid invite"}
      </div>

      <div
        style={{
          fontFamily: "'Primary-Light'",
          fontSize: "var(--inyc-text-sm)",
          color: "var(--inyc-primary-text-color-light)",
          maxWidth: "280px",
          lineHeight: "1.5",
        }}
      >
        {status === "loading" && "getting the open slots for this roster."}
        {status === "submitted" &&
          "thanks. your account was set up and your availability was sent to the venue."}
        {status === "expired" && "this invite link has expired. ask the venue to send you a new one."}
        {status === "error" && "this invite link is not valid or has already been used."}
      </div>
    </div>
  );
};

export default JoinRosterPage;
